// Copyright 2025, University of Maryland, All Rights Reserved
import { SectionCard } from "./SectionCard"
import type { JSONSchema } from '@/api/types/schema'
import { getIconComponent } from '@/utils/icon-utils'
import { parseAnalysisData, type SectionData } from '@/utils/bill-analysis-parser'

interface BillSectionsProps {
  report_data: any // Generic report data - can be monitor_analysis, ai_review, etc.
  schema?: JSONSchema // Optional schema to extract titles from
  className?: string
}

interface ExtendedSectionData extends SectionData {
  icon?: React.ComponentType<any>
}

// Get default icon for a section based on its name
function getDefaultIcon(section_name: string): string {
  const icon_mappings: Record<string, string> = {
    'summary': 'Target',
    'college_park': 'Building2',
    'higher_education': 'School',
    'financial_aid': 'DollarSign',
    'research': 'Search',
    'operations': 'Settings',
    'compliance': 'Shield',
    'infrastructure': 'Building',
    'financial': 'TrendingUp',
    'academic': 'GraduationCap',
    'administrative': 'Users',
    'cost_analysis': 'TrendingUp',
    'agriculture': 'Wheat',
    'environmental': 'TreePine',
    'health': 'Heart',
    'education': 'BookOpen',
    'technology': 'Laptop',
    'transportation': 'Car',
    'housing': 'Home',
    'legal': 'Scale',
    'budget': 'Calculator',
    'timeline': 'Clock',
    'impact': 'Zap',
    'stakeholders': 'Users2',
    'recommendations': 'CheckCircle'
  }

  // Check for exact matches first
  if (icon_mappings[section_name]) {
    return icon_mappings[section_name]
  }

  // Check for partial matches
  for (const [key, icon] of Object.entries(icon_mappings)) {
    if (section_name.includes(key) || key.includes(section_name)) {
      return icon
    }
  }

  // Default icon
  return 'Info'
}

export function BillSections({ report_data, schema, className }: BillSectionsProps) {
  if (!report_data) {
    return (
      <div className={`text-center py-8 text-muted-foreground ${className}`}>
        <p>No report data available.</p>
      </div>
    )
  }

  // DEBUG: Log the actual data being passed
  console.log('=== BillSections DEBUG ===');
  console.log('report_data:', report_data);
  console.log('schema:', schema);
  console.log('report_data type:', typeof report_data);
  console.log('report_data keys:', Object.keys(report_data || {}));

  // Parse the report data dynamically using shared utility
  const sections = parseAnalysisData(report_data, schema)
  
  // DEBUG: Log the parsed sections
  console.log('parsed sections:', sections);
  console.log('sections.length:', sections.length);
  
  // If no sections found, show empty state
  if (sections.length === 0) {
    return (
      <div className={`text-center py-8 text-muted-foreground ${className}`}>
        <p>No sections found in the report data.</p>
        <p className="text-xs mt-2">Debug: Check console for more details</p>
      </div>
    )
  }

  // Convert sections to extended format with icons
  const extended_sections: ExtendedSectionData[] = sections.map(section => {
    // Try to get icon from original data or use default
    const section_value = (report_data as any)[section.section_name] || {}
    const icon = getIconComponent(section_value.icon || getDefaultIcon(section.section_name))
    
    return {
      ...section,
      icon
    }
  })

  return (
    <div className={`grid gap-6 pb-6 ${className}`}>
      {extended_sections.map((section, index) => (
        <SectionCard
          key={section.section_name}
          title={section.title}
          fields={section.fields}
          icon={section.icon}
          className={`z-${(index + 1) * 10}`}
        />
      ))}
    </div>
  )
}